import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ValidationUI from './ValidationUI'
import { Table, AutoSizer, Column } from './DataTable'
import _ from 'lodash'
import {Send} from 'lucide-react'
import './Comment.css'
import { defaultT } from '../utils/i18n'
import {SortDirection} from 'react-virtualized/dist/commonjs/Table'
import { ObjectId } from 'bson'
import moment from 'moment'
import HoverPopover from './HoverPopover'
import {defaultTableRowRenderer} from 'react-virtualized';

const sort = (ids, getValueForId, sortDirection) =>
    _.orderBy(
        ids,
        getValueForId,
        sortDirection === SortDirection.ASC ? 'asc' : 'desc'
    )

class Comment extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: '',
            sortBy: 'date',
            sortDirection: 'DESC'
        }
    }

    // Helper method to find the incomplete comment
    getIncompleteComment() {
        const { allIds, byId } = this.props
        return allIds.find(id => byId[id]?.status === 'incomplete')
    }

    onChange(event) {
        const newValue = event.target.value
        const {remove, push, update} = this.props
        const incompleteCommentId = this.getIncompleteComment()

        this.setState({value: newValue})

        if (newValue === '') {
            // Delete incomplete comment if text is empty
            if (incompleteCommentId) {
                remove(incompleteCommentId)
            }
        } else {
            // Create or update incomplete comment
            if (!incompleteCommentId) {
                // Create new incomplete comment
                const OID = new ObjectId()
                const incompleteId = OID.toString()

                push({
                    id: incompleteId,
                    text: newValue,
                    user: this.props.user,
                    date: moment().format('YYYY-MM-DD HH:mm:ss'),
                    status: 'incomplete'
                })
            } else {
                // Update existing incomplete comment
                update(incompleteCommentId, {
                    text: newValue,
                    date: moment().format('YYYY-MM-DD HH:mm:ss')
                })
            }
        }
    }

    handleCommentClick = (object) => {
        const displayRow = defaultTableRowRenderer(object)
        const message = <div>
                <p>{object.rowData.text}</p>
                <strong>{object.rowData.user?.name}</strong>
                <br />
                <strong>{object.rowData.date}</strong>
        </div>
        return (
            <HoverPopover
                key={object.rowData.id}
                id={'comment-popover'}
                message={message}
                placement={'top'}
            >
                {displayRow}
            </HoverPopover>
        )
    }

    render() {
        const {
            allIds = [], byId = {}, push, update, t = defaultT, user, editable, disabled, textWith,
            placeholder, noRowsMessage, tKeyText, tKeyUser, tKeyDate, validationMessage
        } = this.props

        const sortedIds = this.state.sortBy
            ? sort(allIds, id => byId[id][this.state.sortBy], this.state.sortDirection)
            : allIds

        const sortedObjects = sortedIds && sortedIds.map(id => byId[id])
        const visibleObjects = sortedObjects.filter(obj => obj.status !== 'incomplete');

        return (
            <div className="Comment-container">
                <div className="Comment-panel-body">
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <Table
                                objects={visibleObjects}
                                sort={({ sortBy, sortDirection }) => {
                                    this.setState({
                                        sortBy,
                                        sortDirection
                                    })
                                }}
                                headerRowClassName="AccordionHeaderRow"
                                headerClassName="AccordionHeader"
                                rowClassName="AccordionRow"
                                sortBy={this.state.sortBy}
                                sortDirection={this.state.sortDirection}
                                maxRows={7} width={width}
                                noRowsLabel={t(noRowsMessage || 'noRows')}
                                rowRenderer={this.handleCommentClick}
                            >
                                <Column
                                    flexGrow={1}
                                    width={textWith || 200}
                                    dataKey="text"
                                    label={t(tKeyText || 'comment', { capitalize: true })}
                                    cellDataGetter={({ dataKey, rowData }) => rowData.automatic ? t(_.get(rowData, dataKey)) : _.get(rowData, dataKey)}
                                    className='Comment-DataTable-standard-column'
                                />
                                <Column
                                    flexGrow={0}
                                    width={220}
                                    dataKey="user.name"
                                    label={t(tKeyUser || 'user', { capitalize: true })}
                                    cellDataGetter={({ dataKey, rowData }) => _.get(rowData, dataKey)}
                                    className='Comment-DataTable-standard-column'
                                />
                                <Column
                                    flexGrow={0}
                                    width={180}
                                    dataKey="date"
                                    label={t(tKeyDate || 'date', { capitalize: true })}
                                    className='Comment-DataTable-standard-column'
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </div>
                {
                    editable && !disabled && <div className="Comment-comment">
                    <textarea
                        rows={2}
                        value={this.state.value}
                        placeholder={t(placeholder || 'writeAComment')}
                        onChange={event => {
                            this.onChange(event)
                        }}
                        className="form-control Comment-textarea remove-button-outline"
                    />
                        <button
                            className={
                                this.state.value === ''
                                    ? 'btn btn-default Comment-button'
                                    : 'btn btn-default Comment-button Comment-focus'
                            }
                            type="button"
                            disabled={this.state.value === ''}
                            onClick={() => {
                                const incompleteCommentId = this.getIncompleteComment()
                                if (incompleteCommentId) {
                                    // Update the incomplete comment to complete status
                                    update(
                                        incompleteCommentId,
                                        {date: moment().format('YYYY-MM-DD HH:mm:ss')},
                                        ['status'] // Delete the status property
                                    )
                                    this.setState({value: ''})
                                }
                            }}
                        >
                            <Send size={17} style={{color: '#686972'}}/>
                        </button>
                    </div>
                }
                { this.state.value && <ValidationUI error={t(validationMessage || 'commentValidation')}/> }
            </div>
        )
    }
}

export default Comment

Comment.propTypes = {
    push: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired, // New prop for updating comments
    remove: PropTypes.func.isRequired, // New prop for removing comments
    t: PropTypes.func
}
